import {
	fetchModuleActif,
	Module,
	Produit,
	UserRole,
} from '@mairistem/finances-utils';
import dayjs from 'dayjs';
import type BaseFeuilleSaisie from '../../classes/feuilleSaisies/BaseFeuilleSaisie';
import StorageKey from '../../constants/common/storage';
import FeuilleSaisieCode from '../../constants/feuilleSaisie/feuilleSaisieCode';
import RecensementDemande from '../../constants/simulation/recensementDemande';
import TypeBudget from '../../constants/simulation/typeBudget';
import TypePeriode from '../../constants/simulation/typePeriode';
import TypeReport from '../../constants/simulation/typeReport';
import type TreeRow from '../../interfaces/feuilleSaisie/treeRow';
import type PanelsData from '../../interfaces/simulation/panelsData';
import type Simulation from '../../interfaces/simulation/simulation';

const handleChangeImputation = (
	panelDataChange: PanelsData,
	simulation: Simulation,
	setSimulation: React.Dispatch<React.SetStateAction<Simulation>>,
) => {
	const panelData: PanelsData = {
		prop: {},
		demande: {},
		vote: {},
		exercicePrecedent: {},
		exercicePrecedent2: {},
		ca: {},
	};
	Object.keys(simulation.panelData).forEach((typePeriode) => {
		Object.keys(simulation.panelData[typePeriode]).forEach((key) => {
			if (
				typeof simulation.panelData[typePeriode][key] === 'object' &&
				simulation.panelData[typePeriode][key] !== null
			) {
				// on ne met pas a jour les annees precedentes
				panelData[typePeriode][key] = simulation.panelData[typePeriode][key];
			} else {
				panelData[typePeriode][key] =
					(simulation.panelData[typePeriode][key] ?? 0) +
					(panelDataChange?.[typePeriode]?.[key] ?? 0);
			}
		});
	});

	setSimulation((s) => ({
		...s,
		panelData,
	}));
};

export const handleChangeMontantToSimulation = (
	differencesMontants: any,
	imputation: TreeRow,
	simulation: Simulation,
	setSimulation: React.Dispatch<React.SetStateAction<Simulation>>,
) => {
	const panelDataChanges: PanelsData = {
		prop: {},
		demande: {},
		vote: {},
		ca: {},
	};

	Object.keys(differencesMontants).forEach((typePeriode) => {
		if (imputation.imp_sens === 'D') {
			if (imputation.cha_section === 'I') {
				panelDataChanges[typePeriode].depenseInvestissement =
					differencesMontants[typePeriode];
			} else {
				panelDataChanges[typePeriode].depenseFonctionnement =
					differencesMontants[typePeriode];
				if (
					simulation?.budget?.type !== TypeBudget.COMPTE_ADMINISTRATIF &&
					imputation.cha_nat_bud === 'R' &&
					!imputation.art_code?.startsWith('002')
				) {
					panelDataChanges[typePeriode].depenseReelFonctionnement =
						differencesMontants[typePeriode];

					if (!imputation.art_code?.startsWith('002')) {
						panelDataChanges[typePeriode].epargneBrute =
							-differencesMontants[typePeriode];
					}
				}
			}

			if (
				simulation?.budget?.type !== TypeBudget.COMPTE_ADMINISTRATIF &&
				imputation.cha_code === '012'
			) {
				panelDataChanges[typePeriode].montantChargePersonnel =
					differencesMontants[typePeriode];
			}

			if (
				simulation?.budget?.type !== TypeBudget.COMPTE_ADMINISTRATIF &&
				imputation.cha_nat_bud === 'R' &&
				imputation.art_code?.startsWith('1641')
			) {
				panelDataChanges[typePeriode].remboursementCapitalDette =
					differencesMontants[typePeriode];
			}
		} else if (imputation?.imp_sens === 'R') {
			if (imputation?.cha_section === 'I') {
				panelDataChanges[typePeriode].recetteInvestissement =
					differencesMontants[typePeriode];
			} else {
				panelDataChanges[typePeriode].recetteFonctionnement =
					differencesMontants[typePeriode];

				if (
					imputation.cha_nat_bud === 'R' &&
					!imputation.art_code?.startsWith('002')
				) {
					panelDataChanges[typePeriode].recetteReelFonctionnement =
						differencesMontants[typePeriode];
				}

				if (
					!imputation.art_code?.startsWith('002') &&
					!imputation.art_code?.startsWith('775') &&
					!simulation?.transfertCpta &&
					simulation?.budget?.type !== TypeBudget.COMPTE_ADMINISTRATIF &&
					imputation.cha_nat_bud === 'R'
				) {
					panelDataChanges[typePeriode].epargneBrute =
						differencesMontants[typePeriode];
				}
			}
		}
	});

	handleChangeImputation(panelDataChanges, simulation, setSimulation);
};

const getReportTypeBudget = (simulation: Simulation) => {
	let reportTypeBudget = null;
	switch (simulation.exercice.reportBpBs) {
		case TypeReport.BUDGET_PRIMITIF:
			reportTypeBudget = TypeBudget.BUDGET_PRIMITIF;
			break;
		case TypeReport.BUDGET_SUPPLEMENTAIRE:
			reportTypeBudget = TypeBudget.BUDGET_SUPPLEMENTAIRE;
			break;
		case TypeReport.DECISION_MODIFICATIVE:
			reportTypeBudget = TypeBudget.DECISION_MODIFICATIVE;
			break;
		default:
	}

	return reportTypeBudget;
};

const isBudgetAvecReport = (simulation: Simulation) =>
	simulation.budget.type === getReportTypeBudget(simulation);

// Le report en fonctionnement depend en plus du parametrage de l'exercice
export const shouldAddReportSection = (
	simulation: Simulation,
	isFonctionnement: boolean,
) =>
	isFonctionnement
		? isBudgetAvecReport(simulation) &&
			Boolean(simulation?.exercice?.budReportFonc)
		: isBudgetAvecReport(simulation);

export const shouldAddReport = (
	simulation: Simulation,
	feuille: BaseFeuilleSaisie,
) =>
	shouldAddReportSection(
		simulation,
		feuille.code === FeuilleSaisieCode.FONCTIONNEMENT ||
			feuille.code ===
				FeuilleSaisieCode.FONCTIONNEMENT_PRESENTATION_ALTERNATIVE,
	);

// Indique si la gestion des demandes est active pour la simulation (module
// actif et type de budget correspondant au parametrage de l'exercice)
export const fetchIsDemandeEnabled = async (simulation: Simulation) => {
	const saisieBudgetService = Boolean(simulation.exercice.saisieBudgetService);

	// Optimisation, en CA_CFU, pas de gestion des demandes
	// (sauf en gestion par service, ou le type de budget n'est plus teste)
	if (
		!saisieBudgetService &&
		simulation.budget.type === TypeBudget.COMPTE_ADMINISTRATIF
	)
		return false;

	// On regarde si on a le module d'actif
	const moduleDemandeEnabled = await fetchModuleActif(
		StorageKey.MODULE_DEMANDE,
		Produit.ELABORATION_BUDGET,
		Module.ARBITRAGE_INSCRIPTIONS_BUDGETAIRES,
	);

	// En gestion par service, le parametrage de recensement des demandes
	// par type de budget ne s'applique pas : les demandes sont toujours actives
	let shouldShowDemande = saisieBudgetService;

	// Sinon, on regarde si le type de budget correspond au parametrage de l'exercice
	if (!shouldShowDemande) {
		switch (simulation.exercice.recensementDemande) {
			case RecensementDemande.BP:
				shouldShowDemande =
					simulation.budget.type === TypeBudget.BUDGET_PRIMITIF;
				break;
			case RecensementDemande.BP_BS:
				shouldShowDemande = [
					TypeBudget.BUDGET_PRIMITIF,
					TypeBudget.BUDGET_SUPPLEMENTAIRE,
				].includes(simulation.budget.type);
				break;
			case RecensementDemande.BP_BS_DM:
				shouldShowDemande = [
					TypeBudget.BUDGET_PRIMITIF,
					TypeBudget.BUDGET_SUPPLEMENTAIRE,
					TypeBudget.DECISION_MODIFICATIVE,
				].includes(simulation.budget.type);
				break;
			default:
				break;
		}
	}

	return moduleDemandeEnabled && shouldShowDemande;
};

export const getDefaultPeriode = (
	simulation: Simulation,
	hasDemande: boolean,
) => {
	if (simulation.budget.type === TypeBudget.COMPTE_ADMINISTRATIF) {
		return TypePeriode.CA_CFU;
	}

	if (simulation.vote) return TypePeriode.VOTE;

	// Si on a le module des demandes et qu'on a le bon type de budget
	if (hasDemande) {
		const dateButoirDemande = dayjs(simulation.dateButoirDemande);
		const today = dayjs();

		// Si la date butoir n'est pas renseignee ou qu'elle n'est pas depassee
		if (
			!simulation.dateButoirDemande ||
			dateButoirDemande.isAfter(today) ||
			dateButoirDemande.isSame(today, 'day')
		) {
			return TypePeriode.DEMANDE;
		}
	}

	return TypePeriode.PROPOSE;
};

// L'arbitrage de masse est propose a un Admin/ChargePilotage, avec ou sans
// gestion par service, sauf au CA/CFU (pas d'arbitrage des demandes).
// Necessite une simulation chargee avec son budget (withBudget).
export const isArbitrageMasseDisponible = (
	simulation: Simulation,
	userRole: UserRole,
) =>
	Boolean(simulation?.budget) &&
	simulation.budget.type !== TypeBudget.COMPTE_ADMINISTRATIF &&
	userRole >= UserRole.CHARGE_PILOTAGE;
